import { useEffect, useRef } from 'react'
import type { CloudSeriesRecord } from '@govu/shared'
import type { DeviceSummary } from '../devices/useDevices'
import { LazyImage } from '../ui/LazyImage'
import { useTmdbLazySeries } from './useTmdbLazy'

interface Props {
  tmdbId: number
  record: CloudSeriesRecord
  devices: Map<string, DeviceSummary>
  onOpen: (tmdbId: number, type: 'tv') => void
  focused?: boolean
  onFocus?: () => void
}

export function SeriesCard({
  tmdbId,
  record,
  devices,
  onOpen,
  focused = false,
  onFocus,
}: Props) {
  const { details: tmdb, ref } = useTmdbLazySeries(tmdbId)
  const buttonRef = useRef<HTMLButtonElement | null>(null)

  const isVirtual = record.isVirtual !== false
  const ownerNames = (record.ownerDeviceIds ?? [])
    .map((id) => devices.get(id)?.name)
    .filter((n): n is string => !!n)

  const title = tmdb?.name ?? `TMDB Series #${tmdbId}`
  const year = tmdb?.year
  const poster = tmdb?.posterUrl
  const watchedEpisodeCount = countWatchedEpisodes(record)
  // Defensive coercion (see MovieCard.coerceRating) so a string-shaped
  // rating in Firestore still surfaces as the gold user badge instead of
  // silently falling back to the blue TMDB badge.
  const tmdbRating = coerceRating(tmdb?.rating) ?? 0
  const userRating = coerceRating(record.userRating)

  useEffect(() => {
    if (focused && buttonRef.current && document.activeElement !== buttonRef.current) {
      buttonRef.current.focus({ preventScroll: true })
    }
  }, [focused])

  return (
    <div ref={ref as (el: HTMLDivElement | null) => void} className="group flex flex-col">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => onOpen(tmdbId, 'tv')}
        onFocus={onFocus}
        data-focused={focused || undefined}
        className={`relative block aspect-[2/3] w-full overflow-hidden rounded-lg border bg-gradient-to-br from-bg-secondary to-bg-tertiary shadow-cinematic transition-all duration-300 ease-cinematic focus:outline-none ${
          focused
            ? 'border-accent-gold ring-2 ring-accent-gold scale-[1.03] shadow-cinematic-lg'
            : 'border-border-primary group-hover:scale-[1.03] group-hover:shadow-cinematic-lg'
        }`}
        aria-label={
          year != null
            ? `${title} (${year})${tmdbRating > 0 ? `, TMDB rating ${tmdbRating.toFixed(1)}` : ''}`
            : title
        }
      >
        {poster ? (
          <LazyImage
            src={poster}
            alt={title}
            className="h-full w-full"
            placeholderColor="#1a1a1a"
          />
        ) : (
          <PlaceholderPoster label={tmdb ? title : `TMDB #${tmdbId}`} loading={!tmdb} />
        )}

        {userRating != null ? (
          <div className="absolute left-2 top-2 flex items-center gap-0.5 rounded bg-accent-gold/80 px-1.5 py-0.5 text-[11px] font-semibold text-black shadow-sm backdrop-blur-sm">
            <span className="opacity-75">★</span>
            {userRating}
          </div>
        ) : tmdbRating > 0 ? (
          <div className="absolute left-2 top-2 flex items-center gap-0.5 rounded bg-blue-500/80 px-1.5 py-0.5 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
            <span className="opacity-75">★</span>
            {tmdbRating.toFixed(1)}
          </div>
        ) : null}

        {(watchedEpisodeCount > 0 || (tmdb?.numberOfEpisodes ?? 0) > 0) && (
          <div className="absolute right-2 top-2 rounded bg-accent-primary/80 px-1.5 py-0.5 text-[10px] font-semibold text-black shadow-sm backdrop-blur-sm">
            {watchedEpisodeCount}
            {tmdb?.numberOfEpisodes ? `/${tmdb.numberOfEpisodes}` : ''}
          </div>
        )}

        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-2 pt-8">
          <p className="line-clamp-2 text-sm font-medium leading-tight text-white drop-shadow-lg">
            {title}
          </p>
          <div className="mt-0.5 flex items-center gap-2 text-[11px]">
            {year && <span className="font-medium text-accent-gold drop-shadow">{year}</span>}
            {!isVirtual && (
              <span className="truncate text-white/80 drop-shadow">
                {ownerNames.length > 0 ? `On ${ownerNames.join(', ')}` : 'On another device'}
              </span>
            )}
          </div>
        </div>
      </button>
    </div>
  )
}

/**
 * Normalize a rating from Firestore or TMDB into a positive finite
 * number or null. Mirrors MovieCard.coerceRating — see that helper for
 * the full rationale.
 */
function coerceRating(value: unknown): number | null {
  if (value == null) return null
  const num = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(num) || num <= 0) return null
  return num
}

function PlaceholderPoster({ label, loading }: { label: string; loading: boolean }) {
  return (
    <div className="grid h-full w-full place-items-center px-2 text-center">
      <div className="space-y-2">
        {loading ? (
          <div className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-text-tertiary border-t-accent-primary" />
        ) : (
          <div className="mx-auto text-3xl opacity-60">📺</div>
        )}
        <span className="line-clamp-2 text-xs text-text-tertiary">{label}</span>
      </div>
    </div>
  )
}

/**
 * Counts watched episodes for the series progress badge. Specials
 * (season 0) are excluded so the count lines up with TMDB's
 * `numberOfEpisodes` denominator (which also excludes specials) and
 * users can hit 100% completion without having watched bonus content.
 */
function countWatchedEpisodes(record: CloudSeriesRecord): number {
  if (!record.episodes) return 0
  let count = 0
  for (const [seasonKey, season] of Object.entries(record.episodes)) {
    if (!season) continue
    if (Number(seasonKey) === 0) continue
    for (const ep of Object.values(season)) {
      if (ep?.isWatched) count++
    }
  }
  return count
}
